const logicalCaseId = (value) => {
    if (!/^[1-9]\d*$/.test(String(value ?? ''))) return null;
    const id = Number(value);
    return Number.isSafeInteger(id) ? id : null;
};

const validPath = (value) => typeof value === 'string'
    && value !== '' && !value.startsWith('/')
    && !Array.from(value).some((character) => character.charCodeAt(0) < 32
        || character.charCodeAt(0) === 127 || character === '\\')
    && value.replace(/\/$/, '').split('/').every((part) => part !== '' && part !== '.' && part !== '..');

// Case folder responses are scoped by the server request, unlike firm-wide
// rows which already carry files.case_id. Never infer ownership from S3 IDs.
export const bindLibraryCaseItems = (items, responseCaseId, requestedCaseId) => {
    const id = logicalCaseId(responseCaseId);
    if (id === null || id !== logicalCaseId(requestedCaseId) || !Array.isArray(items)) {
        throw new Error('The case document response could not be matched to this case. Refresh and try again.');
    }
    return items.map((item) => {
        if (item?.case_id != null && logicalCaseId(item.case_id) !== id) {
            throw new Error('The case document response contains conflicting ownership. Refresh and try again.');
        }
        return { ...item, case_id: id };
    });
};

export const resolveLibraryMutationScope = (items) => {
    if (!Array.isArray(items) || items.length === 0) {
        throw new Error('Select an item before making this change.');
    }
    let scope = null;
    const keys = [];
    items.forEach((item) => {
        const key = item?.storage_key || item?.key;
        if (!validPath(key)) {
            throw new Error('This item has no unambiguous storage key. Refresh and try again.');
        }
        let current;
        const caseMatch = key.match(/^cases\/([1-9]\d*)\/(.+)$/);
        if (caseMatch) {
            const caseId = logicalCaseId(item?.case_id);
            if (caseId === null) {
                throw new Error('The case that owns this item is unavailable. Open the case and try again.');
            }
            current = { kind: 'case', caseId, storageCaseId: caseMatch[1] };
        } else if (/^(?:library|DocGen)\/.+/.test(key)
            && !/^(?:library\/)?DocGen\/?$/i.test(key)) {
            if (item?.case_id != null && item.case_id !== '' && item.case_id !== 'library') {
                throw new Error('This item has conflicting case and library ownership. Refresh and try again.');
            }
            current = { kind: 'library', caseId: null, storageCaseId: null };
        } else {
            throw new Error('This item cannot be changed from the Library. Open its owning workspace.');
        }
        if (scope && (scope.kind !== current.kind || scope.caseId !== current.caseId
            || scope.storageCaseId !== current.storageCaseId)) {
            throw new Error('Choose items from one case or from the Library, not a mixed selection.');
        }
        scope = current;
        keys.push(key);
    });
    return { ...scope, keys };
};

export const libraryMutationDefaultDestination = (scope) => {
    const key = scope.keys[0].replace(/\/$/, '');
    return key.substring(0, key.lastIndexOf('/')) || (scope.kind === 'case' ? '{{Name}}' : 'library');
};

export const resolveLibraryMutationDestination = (scope, value) => {
    const target = typeof value === 'string' ? value.trim().replace(/\/$/, '') : '';
    if (!validPath(target)) {
        throw new Error('Enter a valid folder path.');
    }
    if (scope.kind === 'library') {
        if (/^(?:cases|leads|deleted)(?:\/|$)/i.test(target) || target.includes('://')) {
            throw new Error('Choose a Library or DocGen destination for this item.');
        }
        return target;
    }
    const caseMatch = target.match(/^cases\/([1-9]\d*)(?:\/(.*))?$/);
    if (caseMatch) {
        if (caseMatch[1] !== scope.storageCaseId && caseMatch[1] !== String(scope.caseId)) {
            throw new Error('Choose a folder in the same case as this item.');
        }
        // Send a case-relative path so the server resolves the current S3
        // alias itself. The logical case_id remains server-provided metadata.
        return caseMatch[2] || '{{Name}}';
    }
    if (/^(?:library|docgen|cases|leads|deleted)(?:\/|$)/i.test(target) || target.includes('://')) {
        throw new Error('Choose a folder in the same case as this item.');
    }
    return target;
};
